import { Box, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import {
  CalloutText,
  DetailBody,
  DetailCard,
  DetailTitle,
  ErrorNote,
  InfoCallout,
  figmaButtonSx,
} from "../Settlement/settlementStyles";
import { PreMintGround } from "../../services/challenge.service";
import type { usePreMintVerdictSignatures } from "../../hooks/usePreMintVerdictSignatures";

// The judge's side of a server-collected pre-mint determination. Replaces PanelSignatureCollector
// for Governance.judgePreMint whenever the backend's verdict-signature endpoint is up: the judge
// picks the ground and verdict (in the dialog above), clicks Sign, and watches the tally. Nothing
// is pasted and nothing costs gas — the server submits judgePreMint once one verdict has enough.
//
// Grouped by GROUND, not by a single panel: SCORE draws a three-seat panel deciding by two,
// DOCUMENTS a single checker, each with its own panel/threshold/tally. `ground` selects which
// group this renders — the one currently picked in the dialog.

export type ServerPreMintVerdict = ReturnType<typeof usePreMintVerdictSignatures>;

export interface PreMintVerdictSignatureTallyProps {
  lane: ServerPreMintVerdict;
  ground: PreMintGround;
  // The verdict currently picked in the dialog.
  upheld: boolean;
  // Connected wallet, to say whether it holds a seat and has signed.
  viewerAddress?: string | null;
  onCancel: () => void;
}

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const PreMintVerdictSignatureTally = ({
  lane,
  ground,
  upheld,
  viewerAddress,
  onCancel,
}: PreMintVerdictSignatureTallyProps) => {
  const { status, loading, signing, error, sign } = lane;
  const viewer = viewerAddress?.toLowerCase() ?? null;

  if (!status) {
    return (
      <>
        {error && <ErrorNote>{error}</ErrorNote>}
        {loading && (
          <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
            <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
            Reading the panel…
          </PopupProcessingButton>
        )}
        <PopupGhostButton onClick={onCancel} fullWidth>
          Cancel
        </PopupGhostButton>
      </>
    );
  }

  const groupStatus = status.byGround.find((g) => g.ground === ground);
  const panel = groupStatus?.panel ?? [];
  const threshold = groupStatus?.threshold ?? 0;
  const verdicts = groupStatus?.verdicts ?? [];
  const selected = verdicts.find((v) => v.upheld === upheld);
  const signedSelected = new Set(selected?.signers ?? []);
  const count = selected?.count ?? 0;
  const other = verdicts.find((v) => v.upheld !== upheld && v.count > 0);
  // Either verdict at the threshold is enough: the server queues judgePreMint for whichever got there.
  const decided = verdicts.find((v) => v.count >= threshold && threshold > 0) ?? null;
  const viewerIsSeat = !!viewer && panel.includes(viewer);
  const viewerSignedThis = !!viewer && signedSelected.has(viewer);
  const needed = Math.max(0, threshold - count);
  const checkerOnly = threshold === 1;

  return (
    <>
      <DetailCard>
        <DetailTitle>
          {checkerOnly ? "Checker" : "Panel"} — {Math.min(count, threshold)} of {threshold} signature
          {checkerOnly ? "" : "s"} on this verdict
        </DetailTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mt: "6px" }}>
          {panel.map((seat) => {
            const signed = signedSelected.has(seat);
            return (
              <Box key={seat} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {signed ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2E7D32" }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#999" }} />
                )}
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px", color: "#000" }}>
                  {shortAddress(seat)}
                </span>
                {seat === viewer && <span style={{ fontSize: "12px", color: "#888" }}>(you)</span>}
              </Box>
            );
          })}
        </Box>
        {other && (
          <DetailBody style={{ marginTop: "10px", color: "#666" }}>
            Also signed: {other.upheld ? "Uphold" : "Dismiss"} ({other.count} of {threshold}). {checkerOnly ? "The checker" : "Seats"} must sign the same answer for it to count.
          </DetailBody>
        )}
        {!viewerIsSeat && (
          <DetailBody style={{ marginTop: "8px" }}>
            Your wallet does not hold {checkerOnly ? "the checker seat" : "a seat on this panel"} for this ground.{" "}
            {checkerOnly ? "The checker signs" : "The seats above sign"} from their own wallet; this tally updates as
            {checkerOnly ? " it does" : " they do"}.
          </DetailBody>
        )}
      </DetailCard>

      {decided ? (
        <InfoCallout surface="white" tone="progress">
          <CalloutText>
            <strong>{decided.upheld ? "Uphold" : "Dismiss"}</strong> has enough signatures. The server is recording
            the determination on chain — this usually takes a minute or two, and this dialog updates when it lands.
          </CalloutText>
        </InfoCallout>
      ) : (
        needed > 0 && (
          <InfoCallout surface="white">
            <CalloutText>
              {needed} more signature{needed === 1 ? "" : "s"} needed on this verdict. Once{" "}
              {checkerOnly ? "it is" : "they are"} in, the server records it — no gas, and nobody has to submit
              anything.
            </CalloutText>
          </InfoCallout>
        )
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {viewerIsSeat &&
        !decided &&
        (signing ? (
          <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
            <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
            Waiting for your wallet signature…
          </PopupProcessingButton>
        ) : (
          <PopupActionButton
            onClick={() => sign(ground, upheld)}
            disabled={viewerSignedThis}
            variant="contained"
            fullWidth
            sx={figmaButtonSx}
          >
            {viewerSignedThis ? "You have signed this verdict" : "Sign this verdict — no gas"}
          </PopupActionButton>
        ))}
      <PopupGhostButton onClick={onCancel} disabled={signing} fullWidth>
        Close
      </PopupGhostButton>
    </>
  );
};

export default PreMintVerdictSignatureTally;
